import { ProjectResourceField } from 'src/store/state/projects';

/**
 * Note: When CSV files are updated or replaced, we need to ensure that everything
 * that refers to its fields/columns have their references updated (class
 * mappings, relationship mappings). The number or order of columns may change
 * during this process, so we use a certain matching logic.
 *
 * Another important thing to note is that,  we store references to columns by
 * their original uploaded value. This means that a column "a" will have a value
 * of "a" even when it's changed to "b" (see {@link ProjectResourceField})
 *
 * The priority of matching old columns to new columns are:
 * 1) Match based on {@link ProjectResourceField.label} for old/new
 * 2) match based on {@link ProjectResourceField.value} for old/new
 * 3) Match based on old label to new value
 * 4) Match based on old value to new label
 * 5) Match based on order of columns
 *
 * Columns are not matched on an all or nothing basis: each column is matched according to the rules
 * Columns can be matched multiple times, but matching continues until all columns from the original
 * file are matched or found to have no matches.
 *
 * This function isn't used when only the header option is toggled (since we automatically
 * match new/old columns by position for that case)
 *
 */

const MATCH_RULE_A = (
  oldField: ProjectResourceField,
  newField: ProjectResourceField,
  _oldIdx: number,
  _newIdx: number
) => oldField.label === newField.label;
const MATCH_RULE_B = (
  oldField: ProjectResourceField,
  newField: ProjectResourceField,
  _oldIdx: number,
  _newIdx: number
) => oldField.value === newField.value;
const MATCH_RULE_C = (
  oldField: ProjectResourceField,
  newField: ProjectResourceField,
  _oldIdx: number,
  _newIdx: number
) => oldField.label === newField.value;
const MATCH_RULE_D = (
  oldField: ProjectResourceField,
  newField: ProjectResourceField,
  _oldIdx: number,
  _newIdx: number
) => oldField.value === newField.label;
const MATCH_RULE_E = (
  _oldField: ProjectResourceField,
  _newField: ProjectResourceField,
  oldIdx: number,
  newIdx: number
) => oldIdx === newIdx;

const MATCH_RULES = [
  MATCH_RULE_A,
  MATCH_RULE_B,
  MATCH_RULE_C,
  MATCH_RULE_D,
  MATCH_RULE_E,
];

export const getNewFieldsByOldFieldKey = (
  oldFields: ProjectResourceField[],
  newFields: ProjectResourceField[],
  areFieldReferencesKept: boolean
): Record<string, ProjectResourceField> => {
  return Object.values(newFields).reduce<Record<string, ProjectResourceField>>(
    (acc, newField, newIdx) => {
      let matchingOldField: ProjectResourceField | undefined;
      MATCH_RULES.some((matchRule) => {
        const foundOldField = oldFields.find((oldField, oldIdx) => {
          return areFieldReferencesKept
            ? oldIdx === newIdx
            : matchRule(oldField, newField, oldIdx, newIdx);
        });
        if (foundOldField) {
          matchingOldField = foundOldField;
        }
        return Boolean(foundOldField);
      });

      if (matchingOldField) {
        acc[matchingOldField.value] = newField;
      }
      return acc;
    },
    {}
  );
};
